import React from 'react';
import PropTypes from 'prop-types';
import { UX2 } from '@wsb/guac-widget-core';
import dataAids from '../../../../constants/dataAids';
import ProductDetailAssets from '../../products/ProductDetailAssets';
import CheckoutButtons from '../../products/details/CheckoutButtons';
import { ProductReviews } from '../../products/Yotpo';
import CommonDetails from '../CommonDetails';
import getStyles from '../getStyles';
import SimilarProducts from '../SimilarProducts';
import ProductLabels from '../../products/details/ProductLabels';
import SocialSharing from '../../products/details/SocialSharing';
import {
  bestsellerRibbonOptions,
  newProductRibbonOptions
} from '../../../../constants/smartRibbonOptions';

const labelsContainer = {
  '@xs': {
    display: 'block'
  },
  '@sm': {
    display: 'none'
  }
};

function ProductDetail1(props) {
  const {
    Grid,
    Grid: { Cell }
  } = UX2.Component;
  const { Block, Text, Details } = UX2.Element;
  const styles = getStyles(props);
  const { showSimilarProducts, similarProducts = [] } = props;
  const isSmartRibbonShown = props.smartRibbonsBestseller || props.smartRibbonsNewProduct;

  const displayedSmartRibbonKey = () => {
    if (!isSmartRibbonShown) {
      return null;
    }

    const {
      smartRibbonsBestseller,
      smartRibbonsNewProduct,
      smartRibbonsBestsellerOption,
      smartRibbonsNewProductOption,
      product
    } = props;

    // This enforces the smart ribbon priority
    if (smartRibbonsBestseller && product['bestseller?']) return smartRibbonsBestsellerOption;
    if (smartRibbonsNewProduct && product['new_product?']) return smartRibbonsNewProductOption;

    return null;
  };

  const smartRibbonText = () => {
    const { translate = () => {} } = props;

    const saleRibbonOptionsMap = {
      [bestsellerRibbonOptions.BESTSELLER]: translate('products.bestseller'),
      [bestsellerRibbonOptions.TOP_PICK]: translate('products.top_pick'),
      [bestsellerRibbonOptions.HOT_ITEM]: translate('products.hot_item'),
      [newProductRibbonOptions.NEW]: translate('products.new'),
      [newProductRibbonOptions.NEW_ARRIVAL]: translate('products.new_arrival')
    };

    return saleRibbonOptionsMap[displayedSmartRibbonKey()];
  };

  return (
    <Block style={{ position: 'relative' }}>
      <Grid size={ 12 }>
        <Cell xs={ 12 } sm={ 6 }>
          <Block style={ styles.assetsWrapper }>
            <ProductDetailAssets
              renderMode={ props.renderMode }
              product={ props.product }
              currentAssetIndex={ props.currentAssetIndex }
              onClickThumbnail={ props.onClickThumbnail }
              assetChangedByVariantSelection={ props.assetChangedByVariantSelection }
              assetsVisible={ 5 }
              fetching={ props.fetching || props.processing || props.inSession }
              pdpImageCropMethod={ props.pdpImageCropMethod }
              pdpImageShape={ props.pdpImageShape }
              pdpLayout={ props.pdpLayout }
            />
          </Block>
        </Cell>
        <Cell xs={ 12 } sm={ 6 }>
          <Block>
            <Block style={{ marginBottom: 'large' }}>
              <Grid size={ 4 }>
                <Cell style={{ minWidth: '100%' }} xs={ 4 } md={ 3 }>
                  { isSmartRibbonShown && (
                    <Details.Minor
                      data-aid={ dataAids.PDP_SMART_RIBBON }
                      featured
                      style={ styles.smartRibbon.label }
                      children={ smartRibbonText() }
                    />
                  ) }
                  <UX2.Element.Heading.Middle
                    data-aid={ dataAids.PRODUCT_NAME_RENDERED }
                    tag='h1'
                    children={ props.product.name }
                    style={ styles.name }
                    group='Group'
                    featured
                  />
                  { (props.product.ship_free ||
                    props.product.limited_edition ||
                    props.product.free_return) && (
                    <Block style={ labelsContainer }>
                      <ProductLabels product={ props.product } translate={ props.translate } />
                    </Block>
                  ) }
                  <CommonDetails { ...props } showProductLabels={ true } />
                </Cell>
              </Grid>
            </Block>
            <CheckoutButtons
              renderMode={ props.renderMode }
              category={ props.category }
              prices={ props.determinePrice() }
              { ...props.baseState }
              requiredFieldsBlank={ props.requiredFieldsBlank }
              isSelectedVariantInStock={ props.isSelectedVariantInStock }
              handleAddToCart={ props.handleAddToCart }
              handleApplePayClick={ props.handleApplePayClick }
              getOrderData={ props.getVariantData }
              phoneRequired={ props.stripeConfig.require_phone_number }
              isPaymentRequestEnabled={
                props.stripeConfig.with_apple_pay && props.stripeConfig.with_payment_request
              }
              isPoyntPaymentRequestEnabled={ props.poyntConfig.with_apple_pay }
              translate={ props.translate }
              showFacebook={ props.showFacebook }
              showTwitter={ props.showTwitter }
              domainName={ props.domainName }
              setHasAttemptedPurchase={ props.setHasAttemptedPurchase }
            />
            <SocialSharing
              showFacebook={ props.showFacebook }
              showTwitter={ props.showTwitter }
              domainName={ props.domainName }
              translate={ props.translate }
              renderMode={ props.renderMode }
            />
            <Text
              richtext
              data-aid={ dataAids.PRODUCT_DESCRIPTION_RENDERED }
              children={ props.description }
            />
          </Block>
        </Cell>
      </Grid>
      <Grid size={ 12 }>
        <Cell xs={ 12 }>
          <ProductReviews product={ props.product } renderMode={ props.renderMode } />
        </Cell>
      </Grid>
      { showSimilarProducts && similarProducts.length > 0 && <SimilarProducts { ...props } /> }
    </Block>
  );
}

ProductDetail1.propTypes = {
  assetChangedByVariantSelection: PropTypes.bool,
  baseState: PropTypes.object,
  category: PropTypes.string,
  currentAssetIndex: PropTypes.number,
  description: PropTypes.string,
  determinePrice: PropTypes.func,
  fetching: PropTypes.bool,
  handleAddToCart: PropTypes.func,
  handleApplePayClick: PropTypes.func,
  getVariantData: PropTypes.func,
  inSession: PropTypes.bool,
  isSelectedVariantInStock: PropTypes.bool,
  onClickThumbnail: PropTypes.func,
  pdpImageCropMethod: PropTypes.string,
  processing: PropTypes.bool,
  product: PropTypes.object,
  stripeConfig: PropTypes.object,
  poyntConfig: PropTypes.object,
  renderMode: PropTypes.string,
  requiredFieldsBlank: PropTypes.bool,
  translate: PropTypes.func,
  pdpImageShape: PropTypes.string,
  showSimilarProducts: PropTypes.bool,
  similarProducts: PropTypes.array,
  pdpLayout: PropTypes.string.isRequired,
  smartRibbonsBestseller: PropTypes.bool,
  smartRibbonsBestsellerOption: PropTypes.string,
  smartRibbonsNewProduct: PropTypes.bool,
  smartRibbonsNewProductOption: PropTypes.string,
  showFacebook: PropTypes.bool,
  showTwitter: PropTypes.bool,
  domainName: PropTypes.string,
  setHasAttemptedPurchase: PropTypes.func,
  useBruce: PropTypes.bool
};

export default ProductDetail1;
